import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';

export function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passValid = password.length >= 8;
  const confirmValid = confirm === password && confirm.length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!passValid || !confirmValid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ email: string; name: string }>('/auth/accept-invite', { token, password });
      toast('Password set', `Sign in as ${res.data.email}`, 'success');
      navigate('/login');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Could not set password';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'var(--bg)' }}>
      <form onSubmit={submit} style={{ width: 380, maxWidth: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
        <div style={{ marginBottom: 18, textAlign: 'center' }}>
          <div className="logo-badge">CLS</div>
          <div className="logo-sub" style={{ marginTop: 6 }}>Logistics CRM</div>
        </div>
        <div className="modal-title" style={{ textAlign: 'center', marginBottom: 6 }}>Set your password</div>
        <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginBottom: 18 }}>
          Welcome to CLS CRM — choose a password to activate your account.
        </div>

        {!token ? (
          <div className="form-err show" style={{ marginBottom: 12 }}>
            This invite link is missing its token. Use the full link from your invitation email, or ask your administrator to resend it.
          </div>
        ) : (
          <>
            <div className="form-row">
              <label className="form-label">New password (min 8 characters)</label>
              <input
                className={`form-input${password && !passValid ? ' form-input-err' : ''}`}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="form-row">
              <label className="form-label">Confirm password</label>
              <input
                className={`form-input${confirm && !confirmValid ? ' form-input-err' : ''}`}
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            {error ? <div className="form-err show" style={{ marginBottom: 12 }}>{error}</div> : null}

            <button className="btn btn-primary" type="submit" disabled={loading || !passValid || !confirmValid} style={{ width: '100%', justifyContent: 'center' }}>
              {loading ? 'Saving…' : 'Set password & continue'}
            </button>
          </>
        )}

        <div style={{ marginTop: 14, fontSize: 10, color: 'var(--faint)', textAlign: 'center' }}>
          Already set your password? <a href="/login" style={{ color: 'var(--accent)' }}>Sign in</a>
        </div>
      </form>
    </div>
  );
}
